import { type Registry as Services, service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Model, { attr, belongsTo } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type { Hotelinfo } from 'frontend-utils/models';
import { MEALTYPE_ICONS } from 'lumen/utils/constants/general-constants';

// Rate strategy base rate
export default class Rsbaserate extends Model {
  [Type] = 'rsbaserate' as const;

  // OpenAPI spec: https://git.mylighthouse.com/OTA-Insight/pricing-insight/-/blob/master/api/openapi-spec/openapi.yaml
  @belongsTo('hotelinfo', { async: false, inverse: null })
  declare hotelinfo: Hotelinfo;

  @attr('string') declare currency: string;
  @attr('boolean') declare isBar: boolean;
  @attr('boolean') declare isBase: boolean;
  @attr('boolean') declare isCancellable: boolean;
  @attr('number') declare mealtype: keyof typeof MEALTYPE_ICONS;
  @attr('string') declare message: string;
  @attr('string') declare pos: string;
  @attr('number') declare rate: number;
  @attr('string') declare roomname: string;
  @attr('string') declare roomtype: string;

  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedBaseRate() {
    if (this.isSoldOut) {
      return this.intl.t('general.soldout');
    }

    if (this.isMissing) {
      return this.intl.t('general.missing').toString();
    }

    return this.hotelAndMarketService.formatRate(this.rate, {
      longFormat: false,
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedRoomname() {
    if (this.isSoldOut) {
      return this.intl.t('general.soldout');
    }

    if (this.isMissing) {
      return this.intl.t('general.missing').toString();
    }

    return this.roomname;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasNoBaseRate() {
    return this.isMissing || this.isSoldOut;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMissing() {
    return this.message === 'general.missing';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSoldOut() {
    return this.message === 'rates.soldout';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rateTypeIcons() {
    const rateIcons = [];

    if (this.rate) {
      if (!isEmpty(this.rate) && this.rate !== 0) {
        if (this.isBar) {
          rateIcons.push('rates-best-flex');
        } else if (this.isCancellable) {
          rateIcons.push('rates-semi-flex');
        } else {
          rateIcons.push('rates-lowest');
        }
      }

      // mealtype icon
      if (this.mealtype) {
        rateIcons.push(MEALTYPE_ICONS[this.mealtype]);
      }
    }

    return rateIcons;
  }
}
